import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import CustomTooltipTypography from "shared/components/CustomTooltipTypography";
import CustomInformationButton from "shared/components/CustomInformationButton";
import { SelectorOptionsWithTooltip } from "shared/models";
import { MenuBorderColor } from "shared/constants";
import InlineAlert from "shared/components/InlineAlert";
import { CustomTooltip } from "@web-applications/allinga-web-components";

export interface SettingsSelectorProps {
  id?: string;
  options: SelectorOptionsWithTooltip;
  label?: string;
  title?: string;
  tooltip?: string;
  onChange: (value: string) => void;
  selectedValue: string;
  shouldShowInlineAlert?: boolean; // show inline alert
  inlineAlertMsg?: string;
  shouldShowDropdown?: boolean; // should show dropdown when options.length === 1
  size?: "small" | "medium";
}

export default function SettingsSelector({
  id,
  options,
  label = "",
  title = "",
  tooltip = "",
  selectedValue,
  onChange,
  shouldShowInlineAlert = false,
  shouldShowDropdown = true,
  inlineAlertMsg = "",
  size = "medium",
}: SettingsSelectorProps): JSX.Element {
  // store the index of the dropdown option selected
  const [showTooltipIcon, setShowTooltipIcon] = useState(-1);

  const dropdown = (
    <FormControl
      fullWidth
      sx={{
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderRadius: 0,
          },
        },
      }}
      id={id}
    >
      {label && <InputLabel id={`${label}-label`}>{label}</InputLabel>}

      <Select
        value={selectedValue}
        labelId={`${label}-label`}
        label={label}
        disabled={options.length === 1}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 275,
              border: `1px solid ${MenuBorderColor}`,
            },
          },
        }}
        onChange={(event) => onChange(event.target.value)}
        sx={{ height: size === "small" ? "40px" : "56px" }}
      >
        {options.map((option, index) => (
          <MenuItem
            onMouseEnter={() => setShowTooltipIcon(index)}
            onMouseLeave={() => setShowTooltipIcon(-1)}
            key={option.value}
            value={option.value}
          >
            <Stack direction="row" gap={1}>
              <Typography variant="body1">{option.name}</Typography>
              {option.optionTooltip && showTooltipIcon === index && (
                <CustomTooltip
                  title={option.optionTooltip}
                  placement="right"
                  disableHoverListener={false}
                  forceShow
                >
                  <CustomInformationButton />
                </CustomTooltip>
              )}
            </Stack>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Stack gap={2}>
      {title && <CustomTooltipTypography title={title} tooltip={tooltip} />}

      {options.length === 0 ? (
        <InlineAlert id="inline-alert" message={inlineAlertMsg} />
      ) : options.length === 1 && shouldShowDropdown ? (
        dropdown
      ) : options.length === 1 && shouldShowInlineAlert ? (
        <InlineAlert id="inline-alert" message={inlineAlertMsg} />
      ) : options.length === 1 &&
        !shouldShowInlineAlert &&
        !shouldShowDropdown ? null : (
        dropdown
      )}
    </Stack>
  );
}
